import { Injectable } from '@angular/core';
import DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root'
})
export class PdfSecurityService {
    private readonly MIN_PDF_SIZE_BYTES = 100; // If file size is less than 100 bytes (≈0.1 KB), treat it as invalid
  
  async validateAndSanitizePDF(
    file: File, 
    options?: { skipESignatureCheck?: boolean }
  ): Promise<{
    isValid: boolean;
    reason?: string;
    isESigned?: boolean;
  }> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const textContent = new TextDecoder('latin1').decode(uint8Array);

      // 1. Check PDF header
      if (!this.isValidPDFHeader(uint8Array)) {
        return { 
          isValid: false, 
          reason: 'Invalid PDF format - corrupted or fake PDF file',
          isESigned: false
        };
      }

      // 2. Check file size anomalies
      if (file.size < this.MIN_PDF_SIZE_BYTES) {
        return { 
          isValid: false, 
          reason: 'Invalid file - file is too small to be a valid PDF',
          isESigned: false
        };
      }
      
      if (file.size > 15 * 1024 * 1024) {
        return {
          isValid: false,
          reason: 'File exceeds 15MB limit',
          isESigned: false
        };
      }

      // 3. CHECK FOR E-SIGNATURES FIRST
    if (!options?.skipESignatureCheck) {
      const isDigitallySigned = this.isDigitallySignedPDF(textContent);
      if (isDigitallySigned) {
        return { 
          isValid: false, 
          reason: 'Document is already signed',
          isESigned: true
        };
      }
      }

      // 4. Check for polyglot files
      if (this.isPolyglotFile(uint8Array)) {
        return { 
          isValid: false, 
          reason: 'Security threat - file appears to be multiple formats',
          isESigned: false
        };
      }

      // 5. Detect ANY JavaScript (STRICT CHECK)
      if (this.containsJavaScript(textContent)) {
        return { 
          isValid: false, 
          reason: 'Security threat detected - PDF contains JavaScript',
          isESigned: false
        };
      }

      // 6. Detect embedded executables
      if (this.containsEmbeddedExecutables(textContent, uint8Array)) {
        return { 
          isValid: false, 
          reason: 'Security threat detected - PDF contains embedded executable files',
          isESigned: false
        };
      }

      // 7. Detect DANGEROUS auto-execute actions
      if (this.containsDangerousAutoActions(textContent)) {
        return { 
          isValid: false, 
          reason: 'Security threat detected - PDF contains auto-execute actions',
          isESigned: false
        };
      }

      // 8. Detect excessive object nesting
      if (this.hasExcessiveObjectNesting(textContent)) {
        return { 
          isValid: false, 
          reason: 'Security threat - PDF structure indicates potential exploit',
          isESigned: false
        };
      }

      return { isValid: true, isESigned: false };

    } catch (error) {
      console.error('PDF validation error:', error);
      return { 
        isValid: false, 
        reason: 'Unable to validate PDF - file may be corrupted',
        isESigned: false
      };
    }
  }

  // Check valid PDF header
  private isValidPDFHeader(data: Uint8Array): boolean {
    const header = String.fromCharCode(...data.slice(0, 5));
    return header === '%PDF-';
  }
  // Check for digital signatures (e-signed PDFs) - in PdfSecurityService
  private isDigitallySignedPDF(content: string): boolean {
    const subFilterMatch = content.match(/\/SubFilter\s*\/(\S+)/i);
   
     // Block only user-applied signatures
     // adbe.pkcs7.sha1 intentionally excluded — auto-applied by PDF generation tools
     const blockedSubFilters = [
       'adbe\\.pkcs7\\.detached',
       'adbe\\.x509\\.rsa_sha1',
       'ETSI\\.CAdES\\.detached',
       'ETSI\\.RFC3161',
     ];
   
     const subFilterPattern = new RegExp(
       `\\/SubFilter\\s*\\/(${blockedSubFilters.join('|')})`,
       'i'
     );
   
     const hasBlockedSubFilter = subFilterPattern.test(content);
     const hasAppliedByteRange = /\/ByteRange\s*\[\s*0\s+\d+\s+\d+\s+\d+\s*\]/i.test(content);
     return hasBlockedSubFilter && hasAppliedByteRange;
  }

  // CRITICAL FIX: Detect ANY JavaScript in PDF
  private containsJavaScript(content: string): boolean {
    // Check for JavaScript action type (most reliable indicator)
    if (/\/S\s*\/JavaScript/i.test(content)) {
      return true;
    }

    // Check for /JS dictionary entry (contains JavaScript code)
    if (/\/JS\s*\(/i.test(content)) {
      return true;
    }

    // Check for /JavaScript name tree
    if (/\/JavaScript\s*\(/i.test(content)) {
      return true;
    }

    // Check for /Names /JavaScript (named JavaScript)
    if (/\/Names\s*<<.*?\/JavaScript/is.test(content)) {
      return true;
    }

    // Check for OpenAction with JavaScript
    if (/\/OpenAction.*?\/S\s*\/JavaScript/is.test(content)) {
      return true;
    }

    // Check for AA (Additional Actions) with JavaScript
    if (/\/AA\s*<<.*?\/S\s*\/JavaScript/is.test(content)) {
      return true;
    }

    // Check for common JavaScript API calls (even if obfuscated)
    const jsApiPatterns = [
      /app\\*\.alert/i,          // app.alert or app\.alert
      /app\\*\.execMenuItem/i,   // app.execMenuItem
      /app\\*\.launchURL/i,      // app.launchURL
      /this\\*\.submitForm/i,    // this.submitForm
      /this\\*\.exportDataObject/i, // this.exportDataObject
      /util\\*\.printf/i,        // util.printf
      /getURL\\*\s*\(/i,         // getURL(
      /eval\\*\s*\(/i,           // eval(
      /unescape\\*\s*\(/i,       // unescape(
    ];

    if (jsApiPatterns.some(pattern => pattern.test(content))) {
      return true;
    }

    return false;
  }

  // Detect DANGEROUS auto-execute actions
  private containsDangerousAutoActions(content: string): boolean {
    const dangerousActions = [
      // Launch executable files
      /\/Launch\s*<<[^>]*\/F\s*\([^)]*\.(exe|bat|cmd|vbs|ps1|scr|com|pif|msi)/i,
      
      // Auto-launch on open (even without JavaScript)
      /\/OpenAction\s*<<[^>]*\/S\s*\/Launch/is,
      
      // Additional Actions with Launch
      /\/AA\s*<<[^>]*\/S\s*\/Launch/is,
      
      // Remote GoTo with executable
      /\/GoToR\s*<<[^>]*\/F\s*\([^)]*\.(exe|bat|cmd)/i,
      
      // Submit to suspicious URLs
      /\/SubmitForm[^>]*\/F\s*\(\s*(?!https:\/\/)/i,
      
      // Import data from external file
      /\/ImportData\s*<<[^>]*\/S\s*\/ImportData/i,
    ];

    return dangerousActions.some(pattern => pattern.test(content));
  }

  // Detect embedded executables
  private containsEmbeddedExecutables(content: string, data: Uint8Array): boolean {
    // Check for embedded files with executable extensions
    const executableFilePattern = /\/EmbeddedFile[^>]*\/(exe|bat|cmd|vbs|ps1|dll|scr|com|pif|msi)/i;
    if (executableFilePattern.test(content)) {
      return true;
    }

    // Check for embedded file with dangerous type
    if (/\/Type\s*\/EmbeddedFile/i.test(content)) {
      // Look for file specification nearby
      const embeddedFileContext = content.match(/\/EmbeddedFile[\s\S]{0,500}\/F\s*\([^)]+\)/i);
      if (embeddedFileContext) {
        const fileSpec = embeddedFileContext[0];
        if (/\.(exe|bat|cmd|vbs|ps1|dll|scr|com|pif|msi)/i.test(fileSpec)) {
          return true;
        }
      }
    }

    // Check binary data for executable headers
    const dataStr = String.fromCharCode(...data.slice(0, Math.min(10000, data.length)));
    
    // MZ header (Windows PE executable)
    if (/MZ[\x00-\xFF]{58}PE\x00\x00/.test(dataStr)) {
      return true;
    }
    
    // ELF header (Linux executable)
    if (/\x7FELF/.test(dataStr)) {
      return true;
    }

    return false;
  }

  // Check for excessive object nesting (zip bomb)
  private hasExcessiveObjectNesting(content: string): boolean {
    const objectCount = (content.match(/\d+ \d+ obj/g) || []).length;
    
    if (objectCount > 20000) {
      return true;
    }

    const streamCount = (content.match(/\/Filter\s*\/FlateDecode/gi) || []).length;
    if (streamCount > 1500 && objectCount > 8000) {
      return true;
    }

    return false;
  }

  // Check for polyglot files
  private isPolyglotFile(data: Uint8Array): boolean {
    if (data.length < 50) return false;

    const header = String.fromCharCode(...data.slice(0, 50));
    
    if (!header.startsWith('%PDF-')) {
      return false;
    }

    const polyglotSignatures = [
      'PK\x03\x04',
      '\x89PNG\r\n\x1a\n',
      'GIF89a',
      'GIF87a',
      // '\xFF\xD8\xFF',
      'MZ',
      '\x7FELF'
    ];

    const searchArea = String.fromCharCode(...data.slice(0, Math.min(1000, data.length)));
    
    return polyglotSignatures.some(sig => {
      const index = searchArea.indexOf(sig);
      return index > 0 && index < 500;
    });
  }

   async generateSecurePreview(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const textContent = new TextDecoder('latin1').decode(uint8Array);
      
      const previewText = textContent.substring(0, 1000);
      const sanitized = DOMPurify.sanitize(previewText, { ALLOWED_TAGS: [] });
      
      if (!sanitized || sanitized.length < 10) {
        return 'PDF uploaded securely. Preview not available.';
      }
      return sanitized;
    } catch (error) {
      console.error('Preview generation error:', error);
      return 'PDF uploaded securely. Use download to view full content.';
    }
  }
}
